import {
  MigrateOption,
  V2Balance,
  V2Position,
  V2Vault,
  V2XSlpPosition,
} from '@/core/migrate';
import fetchV2Balances from './fetchV2Balances';
import fetchV2XSlpPositions from './fetchV2XSlpPositions';
import {
  Address,
  Provider,
  VAULT_FEE_TIER,
  Vault,
  WeiPerEther,
  addressEqual,
  fetchVaults,
  getChainConstant,
  getContract,
} from 'nftx.js';
import fetchV2Vaults from './fetchV2Vaults';
import fetchV2PositionReserves from './fetchV2PositionReserves';
import { calculateLiquidity } from '../positions';
import NftxV2LpStaking from '@/domain/migrate/abis/NftxV2LpStaking';
import { NFTX_INVENTORY_STAKING, NFTX_LP_STAKING } from '@/domain/migrate';
import NftxV2InventoryStakingAbi from '@/domain/migrate/abis/NftxV2InventoryStakingAbi';

const fetchLpMigrateOptions = async ({
  v3Vaults,
  eth,
  network,
  slp,
  vToken,
}: {
  v3Vaults: Pick<Vault, 'vTokenToEth' | 'id' | 'vaultId'>[];
  vToken: bigint;
  eth: bigint;
  network: number;
  slp: bigint;
}): Promise<[MigrateOption[], reason: string]> => {
  // This is a list of migratable vaults
  const migrateOptions: MigrateOption[] = [];
  let reason = '';

  if (!v3Vaults.length) {
    reason = 'There are no v3 vaults that can be migrated to';
    return [[], reason];
  }
  if (vToken < WeiPerEther) {
    reason = reason || 'Not enough vToken in this position to migrate';
    return [[], reason];
  }

  for (const v3Vault of v3Vaults) {
    let maxEth = 0n;
    let maxVToken = 0n;

    // First work out how much eth we'd need to pair with all of our vToken
    const { addLiqTokenA: ethForVToken } = await calculateLiquidity({
      // FIXME: use the current tick price instead of the TWAP
      amountACurrent: v3Vault.vTokenToEth,
      amountALower: 0n,
      amountAUpper: 0n,
      feeTier: VAULT_FEE_TIER,
      network,
      tokenB: v3Vault.id,
      addLiqAmountB: (vToken / WeiPerEther) * WeiPerEther,
      fullRange: true,
    });
    // If the required eth is greater than the eth we have, we need to adjust the vToken
    if (ethForVToken > eth || ethForVToken === 0n) {
      // We don't have enough ETH to migrate all of our vToken
      // so let's see how much vToken we'd need to migrate all of our ETH
      const { addLiqTokenB: vTokenForEth } = await calculateLiquidity({
        // FIXME: use the current tick price instead of the TWAP
        amountACurrent: v3Vault.vTokenToEth,
        amountALower: 0n,
        amountAUpper: 0n,
        feeTier: VAULT_FEE_TIER,
        network,
        tokenB: v3Vault.id,
        addLiqAmountA: eth,
        fullRange: true,
      });

      if (vTokenForEth > vToken || vTokenForEth === 0n) {
        // So we don't have enough ETH to migrate all of our vToken and we don't have enough vToken to migrate all of our ETH
        reason =
          reason || 'Not enough ETH in this position to pair with the vToken';
        continue;
      } else {
        // Now we know we have enough ETH to cover vTokenForEth, how much do we need to cover vToken rounded to the nearest unit?
        const { addLiqTokenA: ethForVToken2 } = await calculateLiquidity({
          amountACurrent: v3Vault.vTokenToEth,
          amountALower: 0n,
          amountAUpper: 0n,
          feeTier: VAULT_FEE_TIER,
          network,
          tokenB: v3Vault.id,
          addLiqAmountB: (vTokenForEth / WeiPerEther) * WeiPerEther,
          fullRange: true,
        });

        maxEth = ethForVToken2;
        maxVToken = (vTokenForEth / WeiPerEther) * WeiPerEther;
      }
    } else {
      maxEth = ethForVToken;
      maxVToken = (vToken / WeiPerEther) * WeiPerEther;
    }

    if (!maxEth) {
      // We can't balance the liquidity for this vault
      reason =
        reason || 'Not enough ETH in this position to pair with the vToken';
      continue;
    }

    // We need to work out what % of vToken we're removing and then apply that to the SLP
    // const perc = (maxEth * WeiPerEther) / eth;
    // const maxVToken = (vToken * perc) / WeiPerEther;
    const perc = (maxVToken * WeiPerEther) / (vToken || 1n);
    const maxSlp = (slp * perc) / WeiPerEther;

    migrateOptions.push({
      vaultId: v3Vault.vaultId,
      vaultAddress: v3Vault.id,
      maxVToken,
      maxSlp,
      maxEth,
      maxXToken: 0n,
    });
  }

  if (migrateOptions.length) {
    reason = '';
  } else if (!reason) {
    reason = 'There are no v3 vaults that can be migrated to';
  }

  return [migrateOptions, reason];
};

const getStakedLiquidityPosition = async ({
  network,
  userAddress,
  v2Vault,
  v3Vaults,
  xSlpPosition,
  provider,
}: {
  xSlpPosition: V2XSlpPosition;
  v3Vaults: Pick<Vault, 'vTokenToEth' | 'id' | 'vaultId'>[];
  v2Vault: Pick<V2Vault, 'token'>;
  network: number;
  userAddress: Address;
  provider: Provider;
}): Promise<V2Position> => {
  // This is a list of migratable vaults
  let [migrateOptions, reason] = await fetchLpMigrateOptions({
    v3Vaults,
    network,
    slp: xSlpPosition.xSlpBalance,
    vToken: xSlpPosition.liquidityTokens,
    eth: xSlpPosition.liquidityEth,
  });

  if (!reason) {
    // Check whether the positions is still timelocked
    const lpContract = getContract({
      abi: NftxV2LpStaking,
      address: getChainConstant(NFTX_LP_STAKING, network),
      provider,
    });
    const timelock = await lpContract.read.lockedUntil({
      args: [BigInt(xSlpPosition.vaultId), userAddress],
    });
    const now = BigInt(Math.floor(Date.now() / 1000));
    if (timelock > now) {
      reason = 'This position is still timelocked';
    }
  }

  return {
    id: `lp-${xSlpPosition.vaultId}`,
    type: 'xSlp',
    vaultId: xSlpPosition.vaultId,
    apr: xSlpPosition.liquidityApr,
    poolShare: xSlpPosition.liquidityShare,
    vaultAddress: xSlpPosition.vaultAddress,
    vToken: xSlpPosition.liquidityTokens,
    eth: xSlpPosition.liquidityEth,
    value: xSlpPosition.liquidityValue,
    symbol: v2Vault.token.symbol,
    userAddress,
    slp: xSlpPosition.xSlpBalance,
    xToken: 0n,
    migrateOptions,
    migratable: !reason,
    reason,
  };
};

const getStakedInventoryPosition = async ({
  balances,
  userAddress,
  v2Vault,
  v3Vaults,
  xSlpPosition,
  network,
  provider,
}: {
  xSlpPosition: V2XSlpPosition;
  balances: V2Balance[];
  userAddress: Address;
  v2Vault: Pick<V2Vault, 'token'>;
  v3Vaults: Pick<Vault, 'vTokenToEth' | 'id' | 'vaultId'>[];
  network: number;
  provider: Provider;
}): Promise<V2Position> => {
  let reason = '';

  if (!v3Vaults.length) {
    reason = 'There are no v3 vaults that can be migrated to';
  }
  let vToken = xSlpPosition.inventoryTokens;
  // Sometimes there can be a rounding issue where you are short by a very tiny amount
  // you can still unstake the whole amount and the contract will just counter the difference
  // In order to account for this in the UI we need to round up to the nearest whole number
  const wholeValueDiff =
    0n - (vToken - (vToken / WeiPerEther) * WeiPerEther - WeiPerEther);
  if (wholeValueDiff > 0n && wholeValueDiff < 2000n) {
    vToken += wholeValueDiff;
  }

  if (vToken < WeiPerEther) {
    reason = reason || 'Not enough vToken in this position to migrate';
  }
  // We need to grab the xToken balance - this is probably the easiest way (rather than getting the xtoken:vtoken ratio from the contract)
  const balance = balances.find(
    (b) => b.type === 'xToken' && b.vaultId === xSlpPosition.vaultId
  );
  if (!balance) {
    reason = reason || 'Not enough xToken in this position to migrate';
  }
  if (!reason) {
    // Check whether the position is still timelocked
    const ipContract = getContract({
      provider,
      abi: NftxV2InventoryStakingAbi,
      address: getChainConstant(NFTX_INVENTORY_STAKING, network),
    });
    const timelock = await ipContract.read.timelockUntil({
      args: [BigInt(xSlpPosition.vaultId), userAddress],
    });
    const now = BigInt(Math.floor(Date.now() / 1000));
    if (timelock > now) {
      reason = 'This position is still timelocked';
    }
  }
  const xToken = balance?.balance ?? 0n;
  const maxVToken = (vToken / WeiPerEther) * WeiPerEther;
  const perc = (maxVToken * WeiPerEther) / (vToken || 1n);
  const maxXToken = (xToken * perc) / WeiPerEther;

  return {
    id: `ip-${xSlpPosition.vaultId}`,
    type: 'xToken',
    vaultId: xSlpPosition.vaultId,
    apr: xSlpPosition.inventoryApr,
    poolShare: xSlpPosition.inventoryShare,
    vaultAddress: xSlpPosition.vaultAddress,
    vToken,
    eth: 0n,
    value: xSlpPosition.inventoryValue,
    symbol: v2Vault.token.symbol,
    userAddress,
    slp: 0n,
    xToken,
    migrateOptions: v3Vaults.map((v) => ({
      vaultId: v.vaultId,
      vaultAddress: v.id,
      maxVToken: maxVToken,
      maxSlp: 0n,
      maxEth: 0n,
      maxXToken,
    })),
    reason,
    migratable: !reason,
  };
};
const getStakedPositions = async ({
  balances,
  network,
  userAddress,
  v2Vaults,
  v3Vaults: allV3Vaults,
  xSlpPositions,
  provider,
}: {
  network: number;
  xSlpPositions: V2XSlpPosition[];
  balances: V2Balance[];
  userAddress: Address;
  v2Vaults: V2Vault[];
  v3Vaults: Vault[];
  provider: Provider;
}) => {
  const positions: V2Position[] = [];

  // Loop through each LP position and find the corresponding v3 vaults
  for (const xSlpPosition of xSlpPositions) {
    const v2Vault = v2Vaults.find((v) => v.vaultId === xSlpPosition.vaultId);
    if (!v2Vault) {
      // Couldn't find a v2 vault for this position
      // don't even flag this position up for migration as the v2 vault is required
      continue;
    }
    const v3Vaults = allV3Vaults.filter((v) =>
      addressEqual(v.asset.id, v2Vault.asset.id)
    );

    const stakedLiquidityPosition = await getStakedLiquidityPosition({
      network,
      userAddress,
      v2Vault,
      v3Vaults,
      xSlpPosition,
      provider,
    });
    positions.push(stakedLiquidityPosition);

    const stakedInventoryPosition = await getStakedInventoryPosition({
      balances,
      userAddress,
      v2Vault,
      v3Vaults,
      xSlpPosition,
      network,
      provider,
    });
    positions.push(stakedInventoryPosition);
  }

  return positions;
};

const getVTokenPosition = async ({
  balance,
  userAddress,
  v2Vault,
  v3Vaults,
}: {
  balance: V2Balance;
  v2Vault: Pick<V2Vault, 'rawPrice' | 'token'>;
  userAddress: Address;
  v3Vaults: Pick<Vault, 'vaultId' | 'id'>[];
}): Promise<V2Position> => {
  let reason = '';

  if (!v3Vaults.length) {
    reason = 'There are no v3 vaults that can be migrated to';
  }
  if (balance.balance < WeiPerEther) {
    reason = reason || 'Not enough vToken in this position to migrate';
  }

  return {
    id: `v-${balance.vaultId}`,
    type: 'vToken',
    apr: 0,
    poolShare: 0n,
    vaultId: balance.vaultId,
    vaultAddress: balance.address,
    vToken: balance.balance,
    eth: 0n,
    value: (balance.balance * v2Vault.rawPrice) / WeiPerEther,
    symbol: v2Vault.token.symbol,
    userAddress,
    slp: 0n,
    xToken: 0n,
    migrateOptions: v3Vaults.map((v) => ({
      vaultId: v.vaultId,
      vaultAddress: v.id,
      maxVToken: (balance.balance / WeiPerEther) * WeiPerEther,
      maxSlp: 0n,
      maxEth: 0n,
      maxXToken: 0n,
    })),
    reason,
    migratable: !reason,
  };
};
const getSlpPosition = async ({
  balance,
  network,
  provider,
  v2Vault,
  userAddress,
  v3Vaults,
}: {
  balance: V2Balance;
  network: number;
  provider: Provider;
  v3Vaults: Pick<Vault, 'vTokenToEth' | 'id' | 'vaultId'>[];
  userAddress: Address;
  v2Vault: Pick<V2Vault, 'token'>;
}): Promise<V2Position> => {
  const { eth, vToken, value } = await fetchV2PositionReserves({
    balance,
    network,
    provider,
  });

  const [migrateOptions, reason] = await fetchLpMigrateOptions({
    v3Vaults,
    network,
    slp: balance.balance,
    vToken,
    eth,
  });

  return {
    id: `slp-${balance.vaultId}`,
    type: 'slp',
    apr: 0,
    poolShare: 0n,
    vaultId: balance.vaultId,
    vaultAddress: balance.address,
    vToken,
    eth,
    value,
    symbol: v2Vault.token.symbol,
    userAddress,
    slp: balance.balance,
    migrateOptions,
    xToken: 0n,
    reason,
    migratable: !reason,
  };
};
const getUnstakedPositions = async ({
  allV3Vaults,
  balances,
  network,
  provider,
  userAddress,
  v2Vaults,
}: {
  balances: V2Balance[];
  v2Vaults: V2Vault[];
  allV3Vaults: Vault[];
  userAddress: Address;
  network: number;
  provider: Provider;
}) => {
  const positions: V2Position[] = [];

  for (const balance of balances) {
    if (['vToken', 'vTokenWETH'].includes(balance.type) === false) {
      // We only care about vToken and slp balances here
      continue;
    }
    const v2Vault = v2Vaults.find((v) => v.vaultId === balance.vaultId);
    if (!v2Vault) {
      // Can't find the corresponding vault for this position
      continue;
    }
    const v3Vaults = allV3Vaults.filter((v) =>
      addressEqual(v.asset.id, v2Vault.asset.id)
    );
    // There are no vaults in v3 for this collection

    if (balance.type === 'vToken') {
      const position = await getVTokenPosition({
        balance,
        userAddress,
        v2Vault,
        v3Vaults,
      });
      positions.push(position);
    } else if (balance.type === 'vTokenWETH') {
      const position = await getSlpPosition({
        balance,
        network,
        provider,
        v3Vaults,
        userAddress,
        v2Vault,
      });
      positions.push(position);
    }
  }

  return positions;
};

const fetchV2Positions = async ({
  network,
  userAddress,
  provider,
}: {
  network: number;
  userAddress: Address;
  provider: Provider;
}) => {
  const xSlpPositions = await fetchV2XSlpPositions({ network, userAddress });
  const balances = await fetchV2Balances({ network, userAddress });
  const v2Vaults: V2Vault[] = await fetchV2Vaults({ network });
  const allV3Vaults: Vault[] = await fetchVaults({ network });

  let positions: V2Position[] = [];

  positions.push(
    ...(await getStakedPositions({
      balances,
      network,
      userAddress,
      v2Vaults,
      v3Vaults: allV3Vaults,
      xSlpPositions,
      provider,
    })),
    ...(await getUnstakedPositions({
      allV3Vaults,
      balances,
      network,
      provider,
      userAddress,
      v2Vaults,
    }))
  );

  // Filter out dust positions
  positions = positions.filter((p) => {
    return p.vToken > 1000n;
  });

  return positions;
};

export default fetchV2Positions;
